import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { requestsAPI, employeesAPI, locationsAPI, itemsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  ArrowLeft, Save, Plus, Trash2, CheckCircle, Package, Truck, Printer,
  FileText, PenLine, X
} from 'lucide-react';
import { formatDate, getStatusColor } from '../lib/utils';
import { SignaturePad } from '../components/SignaturePad';
import { SearchableCombobox } from '../components/SearchableCombobox';
import { EmployeeQuickAddDialog } from '../components/EmployeeQuickAddDialog';

const DISCLAIMER_TEXT =
  "I acknowledge receipt of the items listed above. I understand that these items are the property of " +
  "TriCorps Security and must be returned upon separation from the company or upon request. " +
  "I agree to maintain these items in good condition and report any loss or damage immediately. " +
  "I understand that failure to return company property may result in payroll deductions as permitted by law.";

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showIssuance, setShowIssuance] = useState(false);
  const [issuanceSubmitting, setIssuanceSubmitting] = useState(false);

  // Quick-add employee (Task 2)
  const [showQuickAddEmployee, setShowQuickAddEmployee] = useState(false);

  // Multi-select category filter for Add Item dialog (Task 3)
  const [selectedCategories, setSelectedCategories] = useState([]);

  const [request, setRequest] = useState({
    employee_id: '',
    location_id: '',
    lines: [],
    needed_by: '',
    notes: ''
  });

  const [newLine, setNewLine] = useState({
    item_id: '',
    variant_sku: '',
    quantity: 1,
    notes: ''
  });

  // Issuance form state
  const [pickerSignature, setPickerSignature] = useState('');
  const [employeeSignature, setEmployeeSignature] = useState('');
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [issuanceNotes, setIssuanceNotes] = useState('');

  useEffect(() => {
    loadReferenceData();
    if (!isNew) {
      loadRequest();
    } else {
      setLoading(false);
    }
  }, [id]);

  const loadReferenceData = async () => {
    try {
      const [empRes, locRes, itemRes] = await Promise.all([
        employeesAPI.list({ is_active: true }),
        locationsAPI.list({ is_active: true }),
        itemsAPI.listAll({ is_active: true })
      ]);
      setEmployees(empRes.data);
      setLocations(locRes.data);
      setItems(itemRes.data);
    } catch (error) {
      console.error('Failed to load reference data:', error);
    }
  };

  const loadRequest = async () => {
    try {
      const response = await requestsAPI.get(id);
      setRequest(response.data);
    } catch (error) {
      toast.error('Failed to load request');
      navigate('/requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!request.employee_id || !request.location_id || request.lines.length === 0) {
      toast.error('Please select employee, location and add at least one item');
      return;
    }
    setSaving(true);
    try {
      const res = await requestsAPI.create({
        employee_id: request.employee_id,
        location_id: request.location_id,
        lines: request.lines.map(l => ({
          item_id: l.item_id, variant_sku: l.variant_sku, quantity: l.quantity, notes: l.notes
        })),
        needed_by: request.needed_by || null,
        notes: request.notes
      });
      toast.success('Request created successfully');
      // Keep window open — navigate to the new request's detail view
      navigate(`/requests/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    } finally {
      setSaving(false);
    }
  };

  const handleIssueWithSignature = async () => {
    if (!disclaimerAccepted) {
      toast.error('You must accept the disclaimer');
      return;
    }
    if (!pickerSignature) {
      toast.error('Picker / Issuer signature is required');
      return;
    }
    if (!employeeSignature) {
      toast.error('Employee signature is required');
      return;
    }
    setIssuanceSubmitting(true);
    try {
      await requestsAPI.issueWithSignature(id, {
        picker_signature: pickerSignature,
        employee_signature: employeeSignature,
        disclaimer_accepted: true,
        notes: issuanceNotes || null
      });
      toast.success('Items issued and request archived');
      setShowIssuance(false);
      loadRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue items');
    } finally {
      setIssuanceSubmitting(false);
    }
  };

  const handlePrintPickList = async () => {
    try {
      const res = await requestsAPI.pickListPdf(id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `pick_list_${request.request_number || id.slice(0, 8)}.pdf`;
      a.click();
    } catch (error) {
      toast.error('Failed to generate pick list');
    }
  };

  const handlePrintIssuanceForm = async () => {
    try {
      const res = await requestsAPI.issuanceFormPdf(id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `issuance_form_${request.request_number || id.slice(0, 8)}.pdf`;
      a.click();
    } catch (error) {
      toast.error('Failed to generate issuance form');
    }
  };

  const addLineItem = () => {
    if (!newLine.item_id || !newLine.variant_sku) {
      toast.error('Please select item and variant');
      return;
    }
    const item = items.find(i => i.id === newLine.item_id);
    setRequest({
      ...request,
      lines: [...request.lines, {
        ...newLine,
        id: Date.now().toString(),
        item_name: item?.name,
        quantity: newLine.quantity
      }]
    });
    setNewLine({ item_id: '', variant_sku: '', quantity: 1, notes: '' });
    setShowAddItem(false);
  };

  const removeLine = (lineId) => {
    setRequest({ ...request, lines: request.lines.filter(l => l.id !== lineId) });
  };

  const selectedItem = items.find(i => i.id === newLine.item_id);

  // Build combobox option arrays (memoized)
  const employeeOptions = useMemo(() => employees.map(e => ({
    _key: e.id,
    _searchStr: `${e.first_name} ${e.last_name} ${e.employee_number || ''} ${e.email || ''}`.toLowerCase(),
    ...e,
  })), [employees]);

  const itemOptions = useMemo(() => {
    // Filter by selected categories (Task 3). Empty selection = show all.
    const source = selectedCategories.length === 0
      ? items
      : items.filter(i => selectedCategories.includes(i.category || 'uncategorized'));
    return source.map(i => ({
      _key: i.id,
      _searchStr: `${i.name} ${i.sku || ''} ${i.category || ''}`.toLowerCase(),
      ...i,
    }));
  }, [items, selectedCategories]);

  // Unique category list driven by current catalog data (Task 3).
  const availableCategories = useMemo(() => {
    const set = new Set();
    items.forEach(i => { if (i.category) set.add(i.category); });
    return Array.from(set).sort();
  }, [items]);

  const toggleCategory = (cat) => {
    setSelectedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const variantOptions = useMemo(() => {
    if (!selectedItem?.variants) return [];
    return selectedItem.variants.map(v => ({
      _key: v.sku,
      _searchStr: `${v.size || ''} ${v.color || ''} ${v.sku}`.toLowerCase(),
      ...v,
    }));
  }, [selectedItem]);

  // Status-driven view flags
  const isFulfilled = !isNew && ['fulfilled', 'issued'].includes(request.status);
  // Warehouse / admin can issue from any non-terminal state
  const canIssue = !isNew && hasRole('admin', 'warehouse') && !['fulfilled', 'rejected', 'cancelled'].includes(request.status);
  const canPrintPickList = !isNew && !['rejected', 'cancelled'].includes(request.status);
  const canPrintIssuanceForm = isFulfilled;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton-pulse"></div>
        <div className="h-96 skeleton-pulse rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="request-detail-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/requests')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="page-title">
              {isNew ? 'New Requisition' : request.request_number}
            </h1>
            {!isNew && (
              <p className="text-slate-500 mt-1">
                For {request.employee_name} &bull; Created {formatDate(request.created_at)}
              </p>
            )}
          </div>
          {!isNew && (
            <Badge className={`ml-4 ${getStatusColor(request.status)}`}>
              {request.status}
            </Badge>
          )}
        </div>

        <div className="flex gap-3 flex-wrap">
          {isNew && (
            <Button onClick={handleSave} disabled={saving} data-testid="submit-request-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Submitting...' : 'Submit Request'}
            </Button>
          )}

          {canIssue && (
            <Button
              onClick={() => {
                setPickerSignature('');
                setEmployeeSignature('');
                setDisclaimerAccepted(false);
                setIssuanceNotes('');
                setShowIssuance(true);
              }}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="issue-btn"
            >
              <PenLine className="w-4 h-4 mr-2" /> Issue & Archive
            </Button>
          )}

          {canPrintPickList && (
            <Button variant="outline" onClick={handlePrintPickList} data-testid="print-pick-list-btn">
              <Printer className="w-4 h-4 mr-2" /> Pick List
            </Button>
          )}

          {canPrintIssuanceForm && (
            <Button variant="outline" onClick={handlePrintIssuanceForm} data-testid="print-issuance-form-btn">
              <FileText className="w-4 h-4 mr-2" /> Issuance Form
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Request Info */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Request Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isNew ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Employee *</Label>
                    <SearchableCombobox
                      items={employeeOptions}
                      value={request.employee_id}
                      onSelect={(v) => setRequest({ ...request, employee_id: v })}
                      placeholder="Search employee by name or ID..."
                      searchPlaceholder="Type to filter employees..."
                      emptyText="No employees found"
                      testId="employee-combobox"
                      addNewLabel="Add New Employee"
                      onAddNew={() => setShowQuickAddEmployee(true)}
                      renderItem={(e) => e ? (
                        <span className="truncate">
                          {e.first_name} {e.last_name} <span className="text-slate-500">({e.employee_number})</span>
                        </span>
                      ) : null}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Warehouse *</Label>
                    <Select
                      value={request.location_id}
                      onValueChange={(value) => setRequest({ ...request, location_id: value })}
                    >
                      <SelectTrigger data-testid="location-select">
                        <SelectValue placeholder="Select warehouse" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map(loc => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.name} ({loc.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Needed By</Label>
                    <Input
                      type="date"
                      value={request.needed_by || ''}
                      onChange={(e) => setRequest({ ...request, needed_by: e.target.value })}
                      data-testid="needed-by-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={request.notes || ''}
                    onChange={(e) => setRequest({ ...request, notes: e.target.value })}
                    placeholder="Additional notes..."
                    data-testid="request-notes-input"
                  />
                </div>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Employee</p>
                  <p className="font-medium">{request.employee_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Requested By</p>
                  <p className="font-medium">{request.manager_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Warehouse</p>
                  <p className="font-medium">{request.location_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Needed By</p>
                  <p className="font-medium">{request.needed_by ? formatDate(request.needed_by) : '-'}</p>
                </div>
                {request.notes && (
                  <div className="col-span-2">
                    <p className="text-slate-500">Notes</p>
                    <p className="font-medium">{request.notes}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status & Signatures Info */}
        {!isNew && (
          <Card>
            <CardHeader>
              <CardTitle>Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Created</p>
                    <p className="text-xs text-slate-500">{formatDate(request.created_at)}</p>
                  </div>
                </div>
                {isFulfilled && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Issued &amp; Archived</p>
                      <p className="text-xs text-slate-500">{formatDate(request.fulfilled_at || request.issued_at || request.updated_at)}</p>
                    </div>
                  </div>
                )}
                {request.disclaimer_accepted && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs font-medium text-emerald-700">Signed &amp; Acknowledged</p>
                    <p className="text-xs text-slate-500 mt-1">Disclaimer accepted. Signatures on file.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Line Items */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{isFulfilled ? 'Issued Items' : 'Request Items'}</CardTitle>
              <CardDescription>
                {isFulfilled
                  ? 'These items have been issued to the employee and the request is archived.'
                  : 'Items requested for this employee'}
              </CardDescription>
            </div>
            {isNew && (
              <Dialog
                open={showAddItem}
                onOpenChange={(o) => {
                  setShowAddItem(o);
                  // Task 3: reset filter when dialog closes/cancels
                  if (!o) {
                    setSelectedCategories([]);
                    setNewLine({ item_id: '', variant_sku: '', quantity: 1, notes: '' });
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button data-testid="add-item-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Item
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Item</DialogTitle>
                    <DialogDescription>Filter by category, then search and select an item and variant to add to the request</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    {availableCategories.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Filter by Category</Label>
                          {selectedCategories.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setSelectedCategories([])}
                              className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                              data-testid="clear-category-filter"
                            >
                              <X className="w-3 h-3" /> Clear ({selectedCategories.length})
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2" data-testid="category-filter-chips">
                          {availableCategories.map(cat => {
                            const active = selectedCategories.includes(cat);
                            return (
                              <button
                                key={cat}
                                type="button"
                                onClick={() => toggleCategory(cat)}
                                data-testid={`category-chip-${cat}`}
                                aria-pressed={active}
                                className={
                                  'px-3 py-1 rounded-full text-xs font-medium border transition-colors capitalize ' +
                                  (active
                                    ? 'bg-brand text-brand-contrast border-brand'
                                    : 'bg-surface text-slate-700 border-slate-300 hover:bg-slate-100')
                                }
                              >
                                {cat.replace(/_/g, ' ')}
                              </button>
                            );
                          })}
                        </div>
                        {selectedCategories.length > 0 && (
                          <p className="text-xs text-slate-500" data-testid="category-filter-summary">
                            Showing {itemOptions.length} item{itemOptions.length === 1 ? '' : 's'} in selected categor{selectedCategories.length === 1 ? 'y' : 'ies'}.
                          </p>
                        )}
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Item</Label>
                      <SearchableCombobox
                        items={itemOptions}
                        value={newLine.item_id}
                        onSelect={(v) => setNewLine({ ...newLine, item_id: v, variant_sku: '' })}
                        placeholder="Search items by name, SKU or category..."
                        searchPlaceholder="Type to filter items..."
                        emptyText="No items found"
                        testId="item-combobox"
                        renderItem={(i) => i ? (
                          <span className="truncate">
                            {i.name}
                            {i.sku && <span className="text-slate-500"> &middot; {i.sku}</span>}
                          </span>
                        ) : null}
                      />
                    </div>
                    {selectedItem && (
                      <div className="space-y-2">
                        <Label>Size / Variant</Label>
                        <SearchableCombobox
                          items={variantOptions}
                          value={newLine.variant_sku}
                          onSelect={(v) => setNewLine({ ...newLine, variant_sku: v })}
                          placeholder="Search variant..."
                          searchPlaceholder="Type size, color or SKU..."
                          emptyText="No variants found"
                          testId="variant-combobox"
                          renderItem={(v) => v ? (
                            <span className="truncate">
                              {v.size}{v.color ? ` - ${v.color}` : ''} <span className="text-slate-500">({v.sku})</span>
                            </span>
                          ) : null}
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Quantity</Label>
                      <Input
                        type="number"
                        min="1"
                        value={newLine.quantity}
                        onChange={(e) => setNewLine({ ...newLine, quantity: parseInt(e.target.value) || 1 })}
                        data-testid="quantity-input"
                      />
                    </div>
                    <Button onClick={addLineItem} className="w-full" data-testid="confirm-add-item">
                      Add to Request
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {request.lines?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>{isFulfilled ? 'Issued Qty' : 'Quantity'}</TableHead>
                  {isNew && <TableHead></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {request.lines.map((line, index) => (
                  <TableRow key={line.id || index}>
                    <TableCell className="font-medium">{line.item_name}</TableCell>
                    <TableCell className="font-mono">{line.variant_sku}</TableCell>
                    <TableCell>
                      {isFulfilled ? (line.quantity_fulfilled || line.quantity) : line.quantity}
                    </TableCell>
                    {isNew && (
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => removeLine(line.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No items added yet</p>
              {isNew && <p className="text-sm">Click "Add Item" to start adding items</p>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Issuance Dialog */}
      <Dialog open={showIssuance} onOpenChange={setShowIssuance}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="issuance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" /> Issue Items to {request.employee_name}
            </DialogTitle>
            <DialogDescription>
              Review items, accept the disclaimer, and capture signatures. Confirming will issue and archive this request in a single step.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Items Summary */}
            <div>
              <p className="text-sm font-medium mb-2">Items Being Issued</p>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Qty</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {request.lines?.map((line, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium text-sm">{line.item_name}</TableCell>
                        <TableCell className="font-mono text-sm">{line.variant_sku}</TableCell>
                        <TableCell className="text-sm">{line.quantity}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-amber-900 mb-2">Liability Disclaimer</p>
              <p className="text-xs text-amber-800 leading-relaxed">{DISCLAIMER_TEXT}</p>
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="disclaimer-check"
                  checked={disclaimerAccepted}
                  onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                  className="w-4 h-4 rounded border-amber-400"
                  data-testid="disclaimer-checkbox"
                />
                <label htmlFor="disclaimer-check" className="text-sm font-medium text-amber-900 cursor-pointer">
                  I have read and accept the above disclaimer
                </label>
              </div>
            </div>

            {/* Signatures */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SignaturePad
                label="Picker / Issuer Signature"
                onSignatureChange={setPickerSignature}
                testId="picker-signature"
              />
              <SignaturePad
                label="Employee Signature"
                onSignatureChange={setEmployeeSignature}
                testId="employee-signature"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={issuanceNotes}
                onChange={(e) => setIssuanceNotes(e.target.value)}
                placeholder="Any additional notes about this issuance..."
                data-testid="issuance-notes"
              />
            </div>

            {/* Submit */}
            <div className="flex gap-3 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowIssuance(false)}>Cancel</Button>
              <Button
                onClick={handleIssueWithSignature}
                disabled={issuanceSubmitting || !disclaimerAccepted || !pickerSignature || !employeeSignature}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-issuance-btn"
              >
                {issuanceSubmitting ? 'Processing...' : 'Issue & Archive'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick-add Employee (Task 2) */}
      <EmployeeQuickAddDialog
        open={showQuickAddEmployee}
        onOpenChange={setShowQuickAddEmployee}
        onCreated={async (emp) => {
          // Refresh employee list and auto-select the new one
          try {
            const res = await employeesAPI.list({ is_active: true });
            setEmployees(res.data);
          } catch (_) { /* no-op */ }
          setRequest(prev => ({ ...prev, employee_id: emp.id }));
        }}
      />
    </div>
  );
}
